import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { dashboardStats, users, roles, subscriptions, adsSettings } from '../data/dummyData';

// Create context
const AppContext = createContext();

// Initial state
const initialState = {
  theme: 'light',
  sidebarCollapsed: false,
  notifications: [],
  unreadNotificationsCount: 0,
  loading: {
    dashboard: false,
    users: false,
    roles: false,
    subscriptions: false,
    ads: false
  },
  data: {
    dashboard: null,
    users: null,
    roles: null,
    subscriptions: null,
    ads: null
  },
  filters: {
    users: {
      status: 'all',
      subscription: 'all',
      search: ''
    },
    roles: {
      search: ''
    }
  },
  sort: {
    users: {
      field: 'joinDate',
      direction: 'desc'
    }
  },
  pagination: {
    users: {
      currentPage: 1,
      itemsPerPage: 10,
      totalItems: 0
    }
  },
  ui: {
    modals: {
      userDetails: false,
      roleDetails: false,
      subscriptionDetails: false,
      deleteConfirmation: false
    },
    activeModal: null,
    selectedItem: null
  }
};

// Action types
const actionTypes = {
  SET_THEME: 'SET_THEME',
  TOGGLE_SIDEBAR: 'TOGGLE_SIDEBAR',
  SET_LOADING: 'SET_LOADING',
  SET_DATA: 'SET_DATA',
  SET_FILTER: 'SET_FILTER',
  SET_SORT: 'SET_SORT',
  SET_PAGINATION: 'SET_PAGINATION',
  SET_MODAL: 'SET_MODAL',
  CLOSE_ALL_MODALS: 'CLOSE_ALL_MODALS',
  SET_SELECTED_ITEM: 'SET_SELECTED_ITEM',
  SET_NOTIFICATIONS: 'SET_NOTIFICATIONS',
  ADD_NOTIFICATION: 'ADD_NOTIFICATION',
  MARK_NOTIFICATION_READ: 'MARK_NOTIFICATION_READ',
  MARK_ALL_NOTIFICATIONS_READ: 'MARK_ALL_NOTIFICATIONS_READ'
};

// Reducer function
function appReducer(state, action) {
  switch (action.type) {
    case actionTypes.SET_THEME:
      return {
        ...state,
        theme: action.payload
      };
      
    case actionTypes.TOGGLE_SIDEBAR:
      return {
        ...state,
        sidebarCollapsed: !state.sidebarCollapsed
      };
      
    case actionTypes.SET_LOADING:
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.payload.section]: action.payload.isLoading
        }
      };
      
    case actionTypes.SET_DATA:
      return {
        ...state,
        data: {
          ...state.data,
          [action.payload.section]: action.payload.data
        }
      };
      
    case actionTypes.SET_FILTER:
      return {
        ...state,
        filters: {
          ...state.filters,
          [action.payload.section]: {
            ...state.filters[action.payload.section],
            [action.payload.filterName]: action.payload.value
          }
        }
      };
      
    case actionTypes.SET_SORT:
      return {
        ...state,
        sort: {
          ...state.sort,
          [action.payload.section]: {
            field: action.payload.field,
            direction: action.payload.direction
          }
        }
      };
      
    case actionTypes.SET_PAGINATION:
      return {
        ...state,
        pagination: {
          ...state.pagination,
          [action.payload.section]: {
            ...state.pagination[action.payload.section],
            ...action.payload.paginationData
          }
        }
      };
      
    case actionTypes.SET_MODAL:
      return {
        ...state,
        ui: {
          ...state.ui,
          modals: {
            ...state.ui.modals,
            [action.payload.modalName]: action.payload.isOpen
          },
          activeModal: action.payload.isOpen ? action.payload.modalName : null
        }
      };
      
    case actionTypes.CLOSE_ALL_MODALS:
      const resetModals = {};
      Object.keys(state.ui.modals).forEach(modal => {
        resetModals[modal] = false;
      });
      
      return {
        ...state,
        ui: {
          ...state.ui,
          modals: resetModals,
          activeModal: null
        }
      };
      
    case actionTypes.SET_SELECTED_ITEM:
      return {
        ...state,
        ui: {
          ...state.ui,
          selectedItem: action.payload
        }
      };
      
    case actionTypes.SET_NOTIFICATIONS:
      return {
        ...state,
        notifications: action.payload,
        unreadNotificationsCount: action.payload.filter(notif => !notif.read).length
      };
      
    case actionTypes.ADD_NOTIFICATION:
      const newNotifications = [action.payload, ...state.notifications];
      return {
        ...state,
        notifications: newNotifications,
        unreadNotificationsCount: newNotifications.filter(notif => !notif.read).length
      };
      
    case actionTypes.MARK_NOTIFICATION_READ:
      const updatedNotifications = state.notifications.map(notif => 
        notif.id === action.payload ? { ...notif, read: true } : notif
      );
      return {
        ...state,
        notifications: updatedNotifications,
        unreadNotificationsCount: updatedNotifications.filter(notif => !notif.read).length
      };
      
    case actionTypes.MARK_ALL_NOTIFICATIONS_READ:
      const allReadNotifications = state.notifications.map(notif => ({ ...notif, read: true }));
      return {
        ...state,
        notifications: allReadNotifications,
        unreadNotificationsCount: 0
      };
      
    default:
      return state;
  }
}

// Provider component
export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState);
  
  // Load initial data (simulating API calls)
  useEffect(() => {
    const sections = ['dashboard', 'users', 'roles', 'subscriptions', 'ads'];
    
    const loadData = async (section) => {
      // Set loading state
      dispatch({
        type: actionTypes.SET_LOADING,
        payload: { section, isLoading: true }
      });
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Map section to data source
      let data;
      switch(section) {
        case 'dashboard':
          data = dashboardStats;
          break;
        case 'users':
          data = users;
          dispatch({
            type: actionTypes.SET_PAGINATION,
            payload: {
              section: 'users',
              paginationData: { totalItems: users.length }
            }
          });
          break;
        case 'roles':
          data = roles;
          break;
        case 'subscriptions':
          data = subscriptions;
          break;
        case 'ads':
          data = adsSettings;
          break;
        default:
          data = null;
      }
      
      // Set data
      dispatch({
        type: actionTypes.SET_DATA,
        payload: { section, data }
      });
      
      // Set loading state to false
      dispatch({
        type: actionTypes.SET_LOADING,
        payload: { section, isLoading: false }
      });
    };
    
    // Load data for each section
    sections.forEach(section => loadData(section));
    
    // Load notifications
    dispatch({
      type: actionTypes.SET_NOTIFICATIONS,
      payload: [
        {
          id: 1,
          title: 'New User Registered',
          message: 'A new user has registered to the platform.',
          time: '5 minutes ago',
          read: false
        },
        {
          id: 2,
          title: 'Subscription Purchase',
          message: 'A user has purchased a premium subscription.',
          time: '1 hour ago',
          read: false
        },
        {
          id: 3,
          title: 'System Update',
          message: 'System successfully updated to the latest version.',
          time: '2 hours ago',
          read: true
        }
      ]
    });
  }, []);
  
  // Action creators
  const setTheme = (theme) => {
    dispatch({
      type: actionTypes.SET_THEME,
      payload: theme
    });
  };
  
  const toggleSidebar = () => {
    dispatch({ type: actionTypes.TOGGLE_SIDEBAR });
  };
  
  const setFilter = (section, filterName, value) => {
    dispatch({
      type: actionTypes.SET_FILTER,
      payload: { section, filterName, value }
    });
  };
  
  const setSort = (section, field, direction) => {
    dispatch({
      type: actionTypes.SET_SORT,
      payload: { section, field, direction }
    });
  };
  
  const setPagination = (section, paginationData) => {
    dispatch({
      type: actionTypes.SET_PAGINATION,
      payload: { section, paginationData }
    });
  };
  
  const openModal = (modalName, selectedItem = null) => {
    if (selectedItem) {
      dispatch({
        type: actionTypes.SET_SELECTED_ITEM,
        payload: selectedItem
      });
    }
    
    dispatch({
      type: actionTypes.SET_MODAL,
      payload: { modalName, isOpen: true }
    });
  };
  
  const closeModal = (modalName) => {
    dispatch({
      type: actionTypes.SET_MODAL,
      payload: { modalName, isOpen: false }
    });
  };
  
  const closeAllModals = () => {
    dispatch({ type: actionTypes.CLOSE_ALL_MODALS });
  };
  
  const addNotification = (notification) => {
    dispatch({
      type: actionTypes.ADD_NOTIFICATION,
      payload: {
        id: Date.now(),
        ...notification,
        read: false
      }
    });
  };
  
  const markNotificationRead = (notificationId) => {
    dispatch({
      type: actionTypes.MARK_NOTIFICATION_READ,
      payload: notificationId
    });
  };
  
  const markAllNotificationsRead = () => {
    dispatch({ type: actionTypes.MARK_ALL_NOTIFICATIONS_READ });
  };
  
  // Get filtered data based on current filters
  const getFilteredData = (section) => {
    if (!state.data[section]) return [];
    
    let filteredData = [...state.data[section]];
    
    // Apply filters
    if (section === 'users' && filteredData.length > 0) {
      const { status, subscription, search } = state.filters.users;
      
      if (status !== 'all') {
        filteredData = filteredData.filter(
          user => user.status.toLowerCase() === status.toLowerCase()
        );
      }
      
      if (subscription !== 'all') {
        filteredData = filteredData.filter(
          user => user.subscriptionStatus.toLowerCase() === subscription.toLowerCase()
        );
      }
      
      if (search) {
        const searchLower = search.toLowerCase();
        filteredData = filteredData.filter(
          user => user.name.toLowerCase().includes(searchLower) || 
                 user.email.toLowerCase().includes(searchLower) ||
                 user.phone.toLowerCase().includes(searchLower)
        );
      }
      
      // Apply sorting
      const { field, direction } = state.sort.users;
      filteredData.sort((a, b) => {
        if (direction === 'asc') {
          return a[field] > b[field] ? 1 : -1;
        } else {
          return a[field] < b[field] ? 1 : -1;
        }
      });
      
      // Apply pagination
      const { currentPage, itemsPerPage } = state.pagination.users;
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      
      return {
        data: filteredData.slice(startIndex, endIndex),
        totalItems: filteredData.length
      };
    }
    
    return filteredData;
  };
  
  const value = {
    // State
    state,
    
    // Selector functions
    getFilteredData,
    
    // Action creators
    setTheme,
    toggleSidebar,
    setFilter,
    setSort,
    setPagination,
    openModal,
    closeModal,
    closeAllModals,
    addNotification,
    markNotificationRead,
    markAllNotificationsRead
  };
  
  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}

// Custom hook to use the app context
export function useAppContext() {
  const context = useContext(AppContext);
  
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  
  return context;
}